import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { FlaskConical } from "lucide-react";
import { useSiteSettings } from "@/hooks/useSiteSettings";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TSHIRT =
  "https://static.prod-images.emergentagent.com/jobs/ded0e787-ce73-4799-bf5f-3aa93c0f5b06/images/7d2efe0218e3b3fac477c5c9930b0d7c7c4b9daf97e0c0a40a0d7bfba4369562.png";
const CD =
  "https://customer-assets.emergentagent.com/job_rock-band-preview/artifacts/9yqwvlyn_image0.jpg";
const LOGO =
  "https://customer-assets.emergentagent.com/job_ded0e787-ce73-4799-bf5f-3aa93c0f5b06/artifacts/qbmi186l_F462A2BF-52F4-48F1-8300-E7C5EAACA372.jpeg";

const PLACEHOLDERS = [
  { id: "p1", name: "Loveless Tee — Black", price: 25, image_url: TSHIRT, status: "coming_soon" },
  { id: "p2", name: "Trapped in a Montana Winter CD", price: 15, image_url: CD, status: "coming_soon" },
  { id: "p3", name: "Tour Trucker Hat", price: 22, image_url: LOGO, status: "coming_soon" },
];

const STATUS_LABEL = {
  available: "Buy Now",
  coming_soon: "Coming Soon",
  sold_out: "Sold Out",
};

export const Merch = () => {
  const settings = useSiteSettings();
  const { data: items = [], isFetched } = useQuery({
    queryKey: ["merch"],
    queryFn: async () => (await axios.get(`${API_BASE}/merch`)).data ?? [],
  });

  // Admin can hide the merch section globally
  if (settings.merch_visible === false) return null;

  // While the store points at Square's test world, buy buttons lead to a
  // checkout that takes no money — say so rather than let someone "buy".
  const testMode = settings.square_mode === "sandbox";

  const display = items.length > 0 ? items : PLACEHOLDERS;
  const isPlaceholder = items.length === 0;

  return (
    <section
      id="merch"
      data-testid="merch-section"
      className="relative bg-[#0A0A0A] py-24 md:py-32 px-6 md:px-16 border-t border-white/5"
    >
      <div className="max-w-[1400px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex items-end justify-between flex-wrap gap-6 mb-14"
        >
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-[#DC2626]" />
              <span className="font-oswald text-xs uppercase tracking-[0.3em] text-[#DC2626]">
                The Merch Stand
              </span>
            </div>
            <h2 className="font-anton uppercase text-white text-5xl md:text-6xl lg:text-7xl leading-[0.9]">
              Band <span className="text-[#B91C1C]">Merch</span>
            </h2>
          </div>
          <p className="max-w-sm text-white/60 font-manrope">
            {settings.merch_note || "Sign up below for promo codes on launch day."}
          </p>
        </motion.div>

        {testMode && (
          <div
            data-testid="merch-test-mode-notice"
            className="flex items-center gap-3 border-2 border-yellow-500 bg-yellow-500/10 px-5 py-4 mb-8"
          >
            <FlaskConical size={20} className="text-yellow-400 flex-shrink-0" />
            <p className="text-white font-manrope text-sm md:text-base">
              <span className="font-oswald uppercase tracking-[0.15em] text-yellow-400">
                Shop is in test mode
              </span>{" "}
              — orders placed right now aren't real and nothing will ship. Check back shortly.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {display.map((m, i) => {
            // Square is the source of truth for stock once an item is published,
            // so an item that has sold out reads as sold out even if nobody has
            // got round to changing its status in the admin.
            const soldOut =
              m.status === "sold_out" ||
              (m.track_inventory !== false && m.square_variation_id && (m.stock ?? 0) <= 0);
            const label = soldOut ? "Sold Out" : STATUS_LABEL[m.status] || "Coming Soon";
            const showLink = !soldOut && m.status === "available" && m.square_buy_url;
            return (
              <motion.div
                key={m.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                data-testid={`merch-item-${m.id}`}
                className="relative group distress-border bg-[#0F0F0F] hover:bg-[#150A0A] transition-colors pt-8"
              >
                <div className="tape">{label}</div>
                <div className="relative aspect-square overflow-hidden mx-4 mt-2 grain-overlay bg-black">
                  {m.image_url && (
                    <img
                      src={m.image_url}
                      alt={m.name}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  )}
                </div>
                <div className="p-5 flex items-end justify-between gap-3">
                  <div>
                    <div className="font-oswald uppercase tracking-wide text-white text-lg">
                      {m.name}
                    </div>
                    <div className="font-anton text-[#DC2626] text-2xl mt-1">
                      ${typeof m.price === "number" ? m.price.toFixed(0) : m.price}
                    </div>
                  </div>
                  {showLink ? (
                    <a
                      href={m.square_buy_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`merch-buy-${m.id}`}
                      className="font-oswald uppercase tracking-[0.18em] text-[10px] bg-[#B91C1C] text-white hover:bg-[#991B1B] px-3 py-2 transition-colors whitespace-nowrap"
                    >
                      Buy Now
                    </a>
                  ) : (
                    <a
                      href="#subscribe"
                      data-testid={`merch-notify-${m.id}`}
                      className="font-oswald uppercase tracking-[0.18em] text-[10px] bg-white text-black hover:bg-[#B91C1C] hover:text-white px-3 py-2 transition-colors whitespace-nowrap"
                    >
                      Notify Me
                    </a>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
        {isPlaceholder && isFetched && (
          <p className="text-center text-white/30 text-xs mt-8 font-manrope">
            Showing preview — add real items in the admin panel.
          </p>
        )}
      </div>
    </section>
  );
};
